/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
  Button, CircularProgress, Box, Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import { api } from '../../../../api';
import { formKeyHandler } from 'utils/formKeyboard';

const TEAL = '#00798E';
const upperSx = { '& .MuiInputBase-input': { textTransform: 'uppercase' } };

/**
 * Quick-add a Store Location from the GRN screen.
 *   props: open, initialName, pharmacy {id,name,code}, onClose(), onCreated(location)
 */
export default function LocationQuickAdd({ open, initialName = '', pharmacy, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', code: '', address: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: (initialName || '').toUpperCase(), code: '', address: '' });
  }, [open, initialName]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value.toUpperCase() }));

  const save = async () => {
    if (!form.name.trim()) { Swal.fire({ icon: 'warning', title: 'Location name is required' }); return; }
    if (!pharmacy?.id) { Swal.fire({ icon: 'warning', title: 'Select a pharmacy first (top-right ribbon)' }); return; }
    setSaving(true);
    try {
      const res = await api.pharmacy.createLocation({
        name: form.name.trim(),
        code: form.code.trim() || `LOC-${Date.now()}`,
        address: form.address.trim() || null,
        is_default: 'N',
        is_active: 'Y',
        irm_pharm_pharmacies_id: pharmacy.id,
      });
      const location = res?.data || res;
      Swal.fire({ icon: 'success', title: 'Location created', toast: true, position: 'top-end', showConfirmButton: false, timer: 1600 });
      onCreated(location);
      onClose();
    } catch { /* handled */ }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ color: TEAL, fontWeight: 700, fontSize: '1rem', borderBottom: '1px solid #f0f2f5', pb: 1.5 }}>
        New Store Location
      </DialogTitle>
      <DialogContent sx={{ pt: '18px !important' }} onKeyDown={formKeyHandler(save)}>
        <Box sx={{ mb: 1.5, px: 1, py: 0.75, borderRadius: 1, bgcolor: `${TEAL}0d`, border: `1px solid ${TEAL}22` }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#555' }}>
            Pharmacy: <b style={{ color: TEAL }}>{pharmacy?.name || '—'}</b>
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TextField autoFocus fullWidth size="small" label="Location Name *" value={form.name} onChange={set('name')} sx={upperSx} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Location Code" value={form.code} onChange={set('code')} helperText="Auto-generated if left blank" sx={upperSx} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Address / Description" value={form.address} onChange={set('address')} multiline rows={2} sx={upperSx} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving}
          sx={{ textTransform: 'none', bgcolor: TEAL, color: 'white !important', fontWeight: 600, minWidth: 130, '&:hover': { bgcolor: '#016476' } }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save (Ctrl+↵)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
